import { useContext, useEffect, useRef, useState } from "react";
import { AppName, dataGridConfig } from "../../../constants";
import DataGrid, {
    Column,
    Scrolling,
    LoadPanel,
    Paging,
} from "devextreme-react/data-grid";
import { CheckBox } from "devextreme-react/check-box";
import { OidcIdentityContext } from "@rsmus/react-auth";
import { createSitesClientApi } from "../../../api-client-nswag/taxportal-client-runtime";
import { GetMDMEntityListDTO, PdcDuoConfigurations } from "../../../api-client-nswag/create-sites-client"
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

export interface selectedDataResponse {
    pending: boolean;
    data: GetMDMEntityListDTO[];
}

// Simple TabPanel helper used by MUI Tabs
function TabPanel(props: { children?: any; index: number; value: number }) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

const LegalEntitesEditPermission = (props: any) => {
    const { user } = useContext(OidcIdentityContext);
    const [arrData, setArrData] = useState<selectedDataResponse>({ pending: true, data: [] });
    const [isAllEntitiesSelected, setIsAllEntitiesSelected] = useState(false);

    // Others tab state and tabs control
    const [othersGridData, setOthersGridData] = useState<any[]>([]);
    const [tabValue, setTabValue] = useState<number>(0);
    const [pdcDuoRows, setPdcDuoRows] = useState<PdcDuoConfigurations[]>([]);
    const [pdcDuoLoading, setPdcDuoLoading] = useState(false);
    const [pdcDuoError, setPdcDuoError] = useState(false);
    const originalPdcDuoRows = useRef<PdcDuoConfigurations[]>([]);

    // Track if Others tab data has been modified
    const [isOthersTabDirty, setIsOthersTabDirty] = useState(false);

    // Track API response flag so we can reapply when switching back to tab 0
    const [responseIsAllEntitySelected, setResponseIsAllEntitySelected] = useState<boolean>(false);

    const handleTabChange = (event: any, newValue: number) => {
        setTabValue(newValue);
        // Notify parent that tab changed - this will be used to determine which SAVE/EDIT logic to use
        if (props.onTabChange) {
            props.onTabChange(newValue);
        }

        // If switching to Others tab, always enable the Edit button
        if (newValue === 1 || newValue === 2) {
            props?.setDisableEditButton?.(false);
        } else {
            // If switching back to Suppress Settings, use the stored response flag
            props?.setDisableEditButton?.(responseIsAllEntitySelected === true);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { hqSiteId, mdmClientId } = props.selectedRowsData[0] || {};
                const response = await createSitesClientApi.mDM_GetSelectedLegalEntities(hqSiteId, mdmClientId);
                const clonedEntities = structuredClone(response?.entities ?? []);

                setArrData((prev) => ({
                    ...prev,
                    pending: false,
                    data: clonedEntities
                }));

                // store the flag from API
                const isAllSelected = response?.isAllEntitySelected === true;
                setResponseIsAllEntitySelected(isAllSelected);

                // Only apply disable logic for the Suppress Settings tab (tab 0)
                if (tabValue === 0) {
                    props?.setDisableEditButton?.(isAllSelected);
                }

                const exclude7216 = response?.exclude7216Consent === true;
                const pdcEnabled = response?.isPDCEnabled === true;
                setOthersGridData([
                    {
                        setting: "Exclude 7216 Logic",
                        value: exclude7216
                    },
                    {
                        setting: "PDC Enabled",
                        value: pdcEnabled
                    }
                ]);


            } catch (err) {
                setArrData({ pending: false, data: [] });
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (tabValue !== 2 || props.hideOthersTab || props.appName !== AppName.TP) return;

        let active = true;
        setPdcDuoLoading(true);
        setPdcDuoError(false);
        createSitesClientApi.pdc_GetLegalEntityPdcDuoConfig(props.selectedRowsData[0]?.hqSiteId)
            .then((response) => {
                if (active) {
                    const rows = response?.data?.pdcDuoConfigurations ?? [];
                    originalPdcDuoRows.current = rows;
                    setPdcDuoRows(rows);
                }
            })
            .catch(() => {
                if (active) setPdcDuoError(true);
            })
            .finally(() => {
                if (active) setPdcDuoLoading(false);
            });
        return () => { active = false; };
    }, [tabValue, props.hideOthersTab, props.appName, props.selectedRowsData]);

    useEffect(() => {
        if (arrData.data != null) {
            const mappedData = arrData.data.map((item: any) => ({
                mdmClientId: props.selectedRowsData[0]?.mdmClientId,
                clientId: item.clientId,
                clientName: item.clientName,
                hqClientName: "",
                hqSiteId: props.selectedRowsData[0]?.hqSiteId,
                suppressFolderCreation: item.suppressFolderCreation,
                suppressDropdown: item.suppressDropdown,
                suppressProjectStatus: item.suppressProjectStatus,
                userName: user?.profile.preferred_username as string,
            }));
            props.setDataPermission(mappedData);
        } else {
            setOthersGridData([]);
        }
    }, [arrData]);

    // renderers for Suppress Settings grid
    const cellRender = (e: any) => {
        return (
            <CheckBox
                defaultValue={
                    arrData.data.length > 0 && e.data.suppressFolderCreation !== undefined && e.data.suppressFolderCreation !== null
                        ? e.data.suppressFolderCreation
                        : false
                }
                onValueChanged={(a) => {
                    const modifiedData = props.dataPermission.map((b: any) => {
                        if (b.clientId === e.data.clientId) {
                            b.suppressFolderCreation = a.value;
                        }
                        return b;
                    });
                    props.setDataPermission(modifiedData);
                    localStorage.setItem("data", JSON.stringify(modifiedData));
                }}
                disabled={!props.handleEdit}
            />
        );
    };
    const cellRender2 = (e: any) => {
        return (
            <CheckBox
                defaultValue={
                    arrData.data.length > 0 && e.data.suppressDropdown !== undefined && e.data.suppressDropdown !== null
                        ? e.data.suppressDropdown
                        : false
                }
                onValueChanged={(a) => {
                    const modifiedData = props.dataPermission.map((b: any) => {
                        if (b.clientId === e.data.clientId) {
                            b.suppressDropdown = a.value;
                        }
                        return b;
                    });
                    props.setDataPermission(modifiedData);
                    localStorage.setItem("data", JSON.stringify(modifiedData));
                }}
                disabled={!props.handleEdit}
            />
        );
    };
    const cellRender3 = (e: any) => {
        return (
            <CheckBox
                defaultValue={
                    arrData.data.length > 0 && e.data.suppressProjectStatus !== undefined && e.data.suppressProjectStatus !== null
                        ? e.data.suppressProjectStatus
                        : false
                }
                onValueChanged={(a) => {
                    const modifiedData = props.dataPermission.map((b: any) => {
                        if (b.clientId === e.data.clientId) {
                            b.suppressProjectStatus = a.value;
                        }
                        return b;
                    });
                    props.setDataPermission(modifiedData);
                    localStorage.setItem("data", JSON.stringify(modifiedData));
                }}
                disabled={!props.handleEdit}
            />
        );
    };

    // cell renderer for Others tab 'Value' column - show an editable checkbox
    const othersValueRender = (e: any) => {
        const checked = !!e.data.value;

        const onChange = (a: any) => {
            const newValue = !!a.value;
            setIsOthersTabDirty(true);
            setOthersGridData((rows) => rows.map((row) =>
                row.setting === e.data.setting ? { ...row, value: newValue } : row
            ));

            if (e.data.setting === "Exclude 7216 Logic") {
                setArrData((prev) => ({
                    ...prev,
                    data: prev.data.map((item: any) => ({ ...item, exclude7216Consent: newValue }))
                }));
                const modifiedData = props.dataPermission.map((item: any) => ({
                    ...item,
                    exclude7216Consent: newValue
                }));
                props.setDataPermission(modifiedData);
            }
        };

        return (
            <CheckBox
                defaultValue={checked}
                onValueChanged={onChange}
                disabled={!props.handleEdit}
            />
        );
    };

    // Expose current tab and Others dirty state to parent via callback
    useEffect(() => {
        const changedPdcDuoRows = pdcDuoRows.filter((row) => {
            const original = originalPdcDuoRows.current.find((item) => item.clientId === row.clientId);
            return original && (original.isPdcEnabled !== row.isPdcEnabled ||
                !!original.isDuoEnabled !== !!row.isDuoEnabled);
        });
        if (props.onStateChange) {
            props.onStateChange({
                activeTab: tabValue,
                isOthersTabDirty,
                othersData: othersGridData,
                pdcDuoRows: changedPdcDuoRows,
                isPdcDuoDirty: changedPdcDuoRows.length > 0
            });
        }
    }, [tabValue, isOthersTabDirty, othersGridData, pdcDuoRows]);

    return (
        <div>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="Legal Entities Settings Tabs"
                    sx={{
                        "& .MuiTab-root": {
                            textTransform: "none",
                            fontWeight: 500,
                        },
                    }}
                >
                    <Tab label="Suppress Settings" {...a11yProps(0)} />
                    {!props.hideOthersTab &&
                        <Tab label="Others" {...a11yProps(1)} />
                    }
                    {!props.hideOthersTab && props.appName === AppName.TP &&
                        <Tab label="PDC Settings" {...a11yProps(2)} />
                    }
                </Tabs>
            </Box>

            {/* Suppress Settings Tab */}
            <TabPanel value={tabValue} index={0}>
                <DataGrid
                    {...dataGridConfig}
                    showBorders={true}
                    height={360}
                    dataSource={props.dataPermission}
                    noDataText={arrData.pending ? "Loading..." : "No Data"}
                >
                    <LoadPanel enabled={false} />
                    <Scrolling mode="infinite" columnRenderingMode="virtual" />
                    <Paging defaultPageSize={5} />
                    <Column
                        width={90}
                        alignment="left"
                        dataField="clientId"
                        caption={"Client Id"}
                    />
                    <Column
                        alignment="left"
                        minWidth={180}
                        dataField="clientName"
                        caption={"Client Name"}
                    />
                    <Column
                        alignment="center"
                        width={230}
                        type={"checkbox"}
                        cellRender={cellRender}
                        caption={"Suppress in Folder creation in Files"}
                    />
                    <Column
                        alignment="center"
                        width={220}
                        cellRender={cellRender2}
                        caption={"Suppress in Information Request"}
                    />
                    <Column
                        alignment="center"
                        width={180}
                        cellRender={cellRender3}
                        caption={"Suppress in Project Status"}
                    />
                </DataGrid>
            </TabPanel>

            {/* Others Tab */}
            {!props.hideOthersTab && (
                <TabPanel value={tabValue} index={1}>
                    <DataGrid
                        {...dataGridConfig}
                        showBorders={true}
                        height={320}
                        dataSource={props.appName === AppName.IS
                            ? othersGridData.filter((row) => row.setting !== "PDC Enabled")
                            : othersGridData}
                        keyExpr="setting"
                        noDataText={arrData.pending ? "Loading..." : "No Data"}
                    >
                        <LoadPanel enabled={false} />
                        <Scrolling mode="infinite" columnRenderingMode="virtual" />
                        <Paging defaultPageSize={5} />
                        <Column
                            alignment="left"
                            minWidth={300}
                            dataField="setting"
                            caption={"Settings"}
                        />
                        <Column
                            alignment="center"
                            minWidth={150}
                            dataField="value"
                            caption={"Value"}
                            cellRender={othersValueRender}
                        />
                    </DataGrid>
                </TabPanel>
            )}
            {!props.hideOthersTab && props.appName === AppName.TP && (
                <TabPanel value={tabValue} index={2}>
                    <DataGrid
                        {...dataGridConfig}
                        repaintChangesOnly={false}
                        height={360}
                        dataSource={pdcDuoRows}
                        keyExpr="clientId"
                        noDataText={pdcDuoLoading ? "Loading..." : pdcDuoError ? "Unable to load PDC settings" : "No Data"}
                    >
                        <LoadPanel enabled={false} />
                        <Scrolling mode="infinite" columnRenderingMode="virtual" />
                        <Column dataField="clientId" caption="Client Id" alignment="left" width={150} />
                        <Column dataField="clientName" caption="Client Name" alignment="left" minWidth={180} />
                        <Column
                            caption="PDC Enabled"
                            alignment="center"
                            cellRender={({ data }: { data: PdcDuoConfigurations }) => (
                                <CheckBox
                                    value={data.isPdcEnabled}
                                    disabled={!props.handleEdit}
                                    onValueChanged={({ value, event }) => {
                                        if (event) setPdcDuoRows((rows) => rows.map((row) =>
                                            row.clientId === data.clientId
                                                ? { ...row, isPdcEnabled: !!value, isDuoEnabled: value ? false : row.isDuoEnabled }
                                                : row
                                        ));
                                    }}
                                />
                            )}
                        />
                        <Column
                            caption="Duo Enabled"
                            alignment="center"
                            cellRender={({ data }: { data: PdcDuoConfigurations }) => (
                                <CheckBox
                                    value={data.isDuoEnabled ?? false}
                                    disabled={!props.handleEdit}
                                    onValueChanged={({ value, event }) => {
                                        if (event) setPdcDuoRows((rows) => rows.map((row) =>
                                            row.clientId === data.clientId
                                                ? { ...row, isDuoEnabled: !!value, isPdcEnabled: value ? false : row.isPdcEnabled }
                                                : row
                                        ));
                                    }}
                                />
                            )}
                        />
                    </DataGrid>
                </TabPanel>
            )}
        </div>
    );
};

export default LegalEntitesEditPermission;